import React, { useState } from 'react';
import { Card, Table, Tabs, Tag, Progress } from 'antd';
import { useQuery } from '@tanstack/react-query';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { OiPageFrame } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { OI_CHART } from '@/components/owner-intelligence/charts/OiChartTheme';

const LAYER_COLORS: Record<string, string> = {
  Procurement: OI_CHART.target,
  Manufacturing: OI_CHART.primary,
  Quality: OI_CHART.comparison,
  Sales: OI_CHART.positive,
  Finance: OI_CHART.negative,
};

const RootCausePage: React.FC = () => {
  const [topic, setTopic] = useState<'profit' | 'cash'>('profit');
  const { formatCurrency } = useCurrency();
  const { data, isLoading, error } = useQuery({
    queryKey: ['owner-intelligence', 'root-cause', topic],
    queryFn: () => ownerIntelligenceService.getRootCause(topic),
  });

  const result = data as {
    factors?: Array<{ rank: number; layer: string; cause: string; count: number; impact_hint?: string }>;
    trace_summary?: string[];
    trace_rows?: Array<{ customer_name: string; invoice_number: string; balance_due: number; overdue_days: number }>;
    total_outstanding?: number;
    total_overdue?: number;
  };

  const maxCount = Math.max(1, ...(result?.factors ?? []).map((f) => f.count));

  return (
    <OiPageFrame
      subtitle="Explainable root cause — why profit or cash changed, with supporting ERP evidence."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Root cause analysis unavailable"
    >
      <Tabs
        activeKey={topic}
        onChange={(k) => setTopic(k as 'profit' | 'cash')}
        items={[
          { key: 'profit', label: 'Profit & operations' },
          { key: 'cash', label: 'Cash & receivables' },
        ]}
      />

      {result?.trace_summary?.length ? (
        <Card title="Trace chain" style={{ marginBottom: 16 }} bordered={false}>
          <div className="oi-flow-chain">
            {result.trace_summary.map((item, i) => (
              <React.Fragment key={i}>
                {i > 0 ? <span className="oi-flow-chain__arrow">→</span> : null}
                <div className="oi-flow-chain__node" style={{ borderColor: OI_CHART.primary }}>
                  <div style={{ fontSize: 11, color: 'var(--oi-text-muted)' }}>Step {i + 1}</div>
                  <div>{item}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </Card>
      ) : null}

      {topic === 'cash' && (result?.total_outstanding != null || result?.total_overdue != null) ? (
        <Card style={{ marginBottom: 16 }} bordered={false}>
          Total outstanding: <span className="oi-mono-value">{formatCurrency(Number(result.total_outstanding ?? 0))}</span>
          {' · '}
          Overdue: <span className="oi-mono-value">{formatCurrency(Number(result.total_overdue ?? 0))}</span>
        </Card>
      ) : null}

      <Card title="Contributing factors" bordered={false}>
        <Table
          size="small"
          rowKey="rank"
          pagination={false}
          dataSource={result?.factors ?? []}
          columns={[
            { title: '#', dataIndex: 'rank', width: 48 },
            {
              title: 'Layer',
              dataIndex: 'layer',
              render: (v: string) => <Tag color={LAYER_COLORS[v] ? undefined : 'default'}>{v}</Tag>,
            },
            { title: 'Cause', dataIndex: 'cause' },
            { title: 'Count', dataIndex: 'count', width: 72 },
            {
              title: 'Magnitude',
              render: (_, row) => (
                <Progress percent={Math.round((row.count / maxCount) * 100)} size="small" showInfo={false} />
              ),
            },
            { title: 'Impact', dataIndex: 'impact_hint', ellipsis: true },
          ]}
        />
      </Card>

      {topic === 'cash' && (result?.trace_rows?.length ?? 0) > 0 ? (
        <Card title="AR evidence" style={{ marginTop: 16 }} bordered={false}>
          <Table
            size="small"
            rowKey={(r) => `${r.invoice_number}-${r.customer_name}`}
            dataSource={result?.trace_rows}
            columns={[
              { title: 'Customer', dataIndex: 'customer_name' },
              { title: 'Invoice', dataIndex: 'invoice_number' },
              { title: 'Balance', dataIndex: 'balance_due', render: (v: number) => formatCurrency(v) },
              { title: 'Overdue days', dataIndex: 'overdue_days' },
            ]}
          />
        </Card>
      ) : null}
    </OiPageFrame>
  );
};

export default RootCausePage;
